import { onDocumentReady } from '../utils/events';

/**
 * Simple accordion: click a header to open/close its panel.
 * Markup: .accordion > .accordion__item > (.accordion__header + .accordion__panel)
 */
onDocumentReady(() => {
  document
    .querySelectorAll('[data-manufacture-accordion] .accordion__panel')
    .forEach((panel) => {
      panel.style.maxHeight = '0px';
      panel.setAttribute('inert', '');
      panel.hidden = false;
    });

  const closeItem = (item) => {
    const header = item.firstElementChild;
    const panel = header?.nextElementSibling;

    item.classList.remove('is-open');
    header?.setAttribute('aria-expanded', 'false');
    if (panel?.classList.contains('accordion__panel'))
      panel.style.maxHeight = '0px';
    if (panel && item.closest('[data-manufacture-accordion]')) {
      panel.setAttribute('inert', '');
    }
  };

  const showClinicalImage = (accordion, itemIndex = 'default') => {
    const section = accordion.closest('.home-clinical');
    const media = section?.querySelector('[data-clinical-media]');
    if (!media) return;

    const requestedImage = media.querySelector(
      `[data-clinical-image="${itemIndex}"]`
    );
    const activeImage =
      requestedImage || media.querySelector('[data-clinical-image="default"]');

    media.querySelectorAll('[data-clinical-image]').forEach((image) => {
      const isActive = image === activeImage;
      image.hidden = !isActive;
      image.classList.toggle('is-active', isActive);
    });
  };

  // Product slug map, kept in sync with contact-prefill.js so the contact form
  // pre-selects the matching product when a visitor lands with ?product=slug.
  const CLINICAL_KEYWORDS = {
    nctf: 'nctf',
    'art-filler': 'art fil', // "Art Filler" (and legacy "Art Filter")
    micronjet: 'micronjet',
    pliaglis: 'pliaglis',
  };

  // Point the "Request a Clinical Study" button at the product the visitor
  // opened, so the contact form arrives pre-filled (EN/FR share this logic).
  const updateClinicalCta = (accordion, itemIndex = 'default') => {
    const section = accordion.closest('.home-clinical');
    const button = section?.querySelector('.home-clinical__content a.button');
    if (!button) return;

    if (!button.dataset.baseHref) {
      button.dataset.baseHref = button.getAttribute('href') || '';
    }

    let slug = '';
    if (itemIndex !== 'default') {
      const item = accordion.querySelector(
        `.accordion__item[data-clinical-item="${itemIndex}"]`
      );
      const label = (
        item?.querySelector('.accordion__label')?.textContent || ''
      ).toLowerCase();
      slug =
        Object.keys(CLINICAL_KEYWORDS).find((k) =>
          label.includes(CLINICAL_KEYWORDS[k])
        ) || '';
    }

    try {
      const url = new URL(button.dataset.baseHref, window.location.href);
      if (slug) {
        url.searchParams.set('product', slug);
      } else {
        url.searchParams.delete('product');
      }
      button.setAttribute('href', url.pathname + url.search + url.hash);
    } catch (e) {
      /* leave the default href untouched if the URL can't be parsed */
    }
  };

  document.addEventListener('click', (event) => {
    const header = event.target.closest('.accordion__header');
    if (!header) return;

    const item = header.closest('.accordion__item');
    const panel = header.nextElementSibling;
    if (!item || !panel?.classList.contains('accordion__panel')) return;

    const accordion = item.closest('.accordion');
    const wasOpen = item.classList.contains('is-open');

    if (accordion?.hasAttribute('data-single-open')) {
      Array.from(accordion.children)
        .filter((openItem) => openItem.classList.contains('is-open'))
        .forEach((openItem) => {
          if (openItem !== item) closeItem(openItem);
        });
    }

    const isOpen = !wasOpen;
    item.classList.toggle('is-open', isOpen);
    header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (accordion?.matches('[data-manufacture-accordion]')) {
      if (isOpen) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    }
    panel.style.maxHeight = isOpen ? `${panel.scrollHeight}px` : '0px';

    if (accordion?.matches('[data-clinical-accordion]')) {
      const activeItem = isOpen ? item.dataset.clinicalItem : 'default';
      showClinicalImage(accordion, activeItem);
      updateClinicalCta(accordion, activeItem);
    }
  });

  // Initialise any item marked open-by-default (server renders `.is-open`).
  const openInitial = () => {
    document.querySelectorAll('.accordion__item.is-open').forEach((item) => {
      const header = item.querySelector('.accordion__header');
      const panel = header?.nextElementSibling;
      header?.setAttribute('aria-expanded', 'true');
      if (panel?.classList.contains('accordion__panel')) {
        // Undo the initial collapse pass so open-by-default panels are
        // both visible and interactive (dev's init marks every panel inert).
        panel.removeAttribute('inert');
        panel.hidden = false;
        panel.style.maxHeight = `${panel.scrollHeight}px`;
      }
    });
  };
  openInitial();
  // Recalculate open panels after fonts/images settle and on resize.
  window.addEventListener('load', openInitial);
  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(openInitial, 150);
  });
});
